<script lang="ts" setup>
import { onMounted, computed, watch, ref } from 'vue'
import { useAgentsLibraryStore } from '@/store/task-library'
import { storeToRefs } from 'pinia'
import { useUIStore } from '@/store/ui-store'
import { useThreadsStore } from '@/store/threads-store'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/store/auth-store'
import { useHeaderVisibility } from '@/composables/use-header-visibility'
import { IAgent } from '@/api/modules/task-library/types'
import TextView from '../../text-view/TextView.vue'
import { Routes } from '@/config/router/routes'
import burger from '@/assets/icons/menu/burger.svg'
import { useChatHistoryPageStore } from '@/store/chat-history-page'
import { Tag } from '@/ui/common/tag'
import { DownOutlined, UpOutlined, CheckOutlined } from '@ant-design/icons-vue'
import { Dropdown, Menu, MenuItem } from 'ant-design-vue'

const authStore = useAuthStore()
const { isAuthenticated } = storeToRefs(authStore)

const { t } = useI18n()

const uiStore = useUIStore()()

const { toggleDrawer } = uiStore

const { isDrawerOpen, isMobile } = storeToRefs(uiStore)

const threadsStore = useThreadsStore()

const { thread, threadAgents } = storeToRefs(threadsStore)
const { getThreadAgents, switchThreadAgent } = threadsStore

const agentsLibraryStore = useAgentsLibraryStore()

const { getAgentsItems } = agentsLibraryStore

const { agentsItems } = storeToRefs(agentsLibraryStore)

const isSwitchingAgent = ref(false)
const isAgentDropdownOpen = ref(false)

const agent = computed(() => {
  return agentsItems.value.find((a: IAgent) => a.name == thread.value?.agent_name)
})

const isLabelExists = computed(() => !!agent.value?.category.label)

const chatHistoryPageStore = useChatHistoryPageStore()

const { categoriesInfoGroupedByCategory } = storeToRefs(chatHistoryPageStore)

const { isShowHeader } = useHeaderVisibility()

const currentAgentId = computed(() => {
  const activeAgent = threadAgents.value.find((a) => a.is_active)
  return activeAgent?.agent_id
})

const agentDropdownItems = computed(() => {
  return threadAgents.value.map((agent) => ({
    key: agent.agent_id,
    label: agent.name,
    agent,
  }))
})

const handleAgentSelect = async ({ key }: { key: string }) => {
  if (!thread.value?.thread_id || key === currentAgentId.value) return

  try {
    isSwitchingAgent.value = true
    await switchThreadAgent(thread.value.thread_id, key)
    await getThreadAgents(thread.value.thread_id)
    await getAgentsItems({ limit: 100 })
  } catch (error) {
    console.error('Failed to switch agent:', error)
  } finally {
    isSwitchingAgent.value = false
  }
}

watch(
  () => thread.value?.thread_id,
  async (threadId) => {
    if (threadId) {
      await getThreadAgents(threadId)
    } else {
      threadAgents.value = []
    }
  },
  { immediate: true },
)

onMounted(async () => {
  if (isAuthenticated.value) {
    await getAgentsItems({ limit: 100 })
  }
})
</script>
<template>
  <div
    :class="[
      'header',
      !isAuthenticated && 'full',
      !isShowHeader && 'hidden-header',
      isLabelExists && 'header_with-border',
    ]"
  >
    <div class="header--left">
      <button v-if="isMobile" @click="() => toggleDrawer(!isDrawerOpen)" class="burger-button">
        <span class="visually-hidden"></span>
        <img :src="burger" alt="burger" />
      </button>

      <Tag
        v-if="isLabelExists"
        :variant="categoriesInfoGroupedByCategory[agent?.category?.code || '']?.category || ''"
        :tagLabel="categoriesInfoGroupedByCategory[agent?.category?.code || '']?.label || ''"
        :text-color="categoriesInfoGroupedByCategory[agent?.category?.code || '']?.text_color || ''"
        :background-color="
          categoriesInfoGroupedByCategory[agent?.category?.code || '']?.background_color || ''
        "
        class="header-tag"
      >
        {{ agent?.category.label }}
      </Tag>
      <span class="delimeter"></span>
      <text-view v-if="agent" variant="roboto_14_semibold" class="header__chat-title">
        <Dropdown
          :trigger="['click']"
          :disabled="isSwitchingAgent || threadAgents.length === 0"
          @open-change="(open: boolean) => (isAgentDropdownOpen = open)"
          @click.prevent
        >
          <a-button type="default" class="agent-dropdown-button" :loading="isSwitchingAgent">
            {{ agent?.name }}
            <DownOutlined v-if="!isSwitchingAgent && threadAgents.length > 0" />
          </a-button>
          <template #overlay>
            <a-menu @click="handleAgentSelect">
              <a-menu-item
                v-for="item in agentDropdownItems"
                :key="item.key"
                :disabled="item.key === currentAgentId"
              >
                <div class="agent-menu-item">
                  <img
                    v-if="item.agent.avatar_url"
                    :src="item.agent.avatar_url"
                    class="agent-avatar"
                    alt=""
                  />
                  <span class="agent-name">{{ item.label }}</span>
                  <check-outlined v-if="item.key === currentAgentId" class="agent-check" />
                </div>
              </a-menu-item>
            </a-menu>
          </template>
        </Dropdown>
      </text-view>
    </div>
    <div class="header__right">
      <a-button v-if="isMobile" type="primary" :href="`../${Routes.Chat}`">
        {{ t('menu.new-chat') }}
      </a-button>
    </div>
  </div>
</template>
<style lang="scss" scoped>
@use '@/assets/styles/media' as media;
.header {
  position: fixed;
  top: 0;
  height: 56px;
  max-height: 56px;
  min-height: 56px;
  justify-content: space-between;
  display: flex;
  align-items: center;
  padding: 0 18px;
  width: calc(100% - 304px);
  z-index: 999;

  &.full {
    width: 100%;
  }

  &_with-border {
    border-bottom: 1px solid var(--gray_20);
  }

  &-tag {
    margin-right: initial;
    height: 20px;
  }

  &--left {
    display: flex;
    align-items: center;
  }

  &__right {
    display: flex;
    gap: 8px;
    flex-direction: row;
    align-items: center;
  }
  &__lang--mobile {
    position: relative;
  }

  & .burger-button {
    cursor: pointer;
  }

  &__chat-title {
    line-height: 24px;
  }

  @include media.max(media.$tablet) {
    width: 100%;
    border-bottom: 1px solid var(--gray_20);

    &-tag,
    .delimeter {
      display: none;
    }

    &__chat-title {
      margin-left: 12px;
    }

    &.full {
      display: none;
    }
  }
}

@include media.min(media.$tablet) {
  .hidden-header {
    position: initial;
    background-color: var(--gray_0);
    border-bottom: initial;
  }
}

.visually-hidden {
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
}

.delimeter {
  display: inline-block;
  font-size: 20px;
  transform: translateY(1.5px);
  margin: 0px 10px 2px;
  border-left: 1px solid;
  line-height: 26px;
  height: 18px;
}

.agent-dropdown-button {
  padding: 4px 8px;
  height: auto;
  font-size: inherit;
  font-weight: inherit;
  color: inherit;

  &:hover,
  &:focus {
    color: var(--primary_50);
  }

  .anticon {
    font-size: 12px;
    margin-left: 4px;
  }
}

.agent-menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 150px;

  .agent-avatar {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    object-fit: cover;
  }

  .agent-name {
    flex: 1;
  }

  .agent-check {
    color: var(--primary_50);
    font-size: 12px;
  }
}
</style>
